"use client";

import { useRef } from "react";
import Icon from "@/components/ui/AppIcon";
import { motion } from "framer-motion";

const bentoItems = [
  {
    id: "comms",
    size: "lg:col-span-2 lg:row-span-1",
    label: "Core Differentiator",
    title: "European standards. China speed.",
    body: "Most China 3PLs are warehouse-first. Helium is brand-first. Founded by Europeans, our team communicates in your timezone, your language, and with the transparency you expect from a Western partner.",
    icon: "GlobeEuropeAfricaIcon",
    accent: true,
  },
  {
    id: "sourcing",
    size: "lg:col-span-1 lg:row-span-1",
    label: "Hybrid Service",
    title: "Sourcing + fulfillment in one",
    body: "Skip the middlemen. We source directly from Shenzhen factories and ship to your customers — one partner, one invoice, zero coordination headaches.",
    icon: "CubeTransparentIcon",
    accent: false,
  },
  {
    id: "nomoq",
    size: "lg:col-span-1 lg:row-span-1",
    label: "Startup Friendly",
    title: "No MOQ. No minimums.",
    body: "Start with 10 orders or 10,000. Our flexible model scales with your brand without requiring upfront volume commitments.",
    icon: "ScaleIcon",
    accent: false,
  },
  {
    id: "speed",
    size: "lg:col-span-1 lg:row-span-1",
    label: "Fast Delivery",
    title: "US & EU in 5–8 days",
    body: "Factory proximity in Shenzhen means faster inbound. Optimized carrier routes mean faster outbound. Your customers get their orders before they forget they ordered.",
    icon: "BoltIcon",
    accent: false,
  },
  {
    id: "tech",
    size: "lg:col-span-2 lg:row-span-1",
    label: "Tech-Driven",
    title: "Real-time tracking. Full visibility.",
    body: "Connect your Shopify, WooCommerce, or TikTok Shop in minutes. Get live order status, inventory alerts, and analytics in your dashboard — no more chasing your 3PL for updates.",
    icon: "ComputerDesktopIcon",
    accent: false,
  },
];

export default function DifferentiatorsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: { once: true, margin: "-50px" }
  };

  const cardVariant = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <section ref={sectionRef} className="py-28 bg-deep-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div className="text-center mb-16" {...fadeInUp}>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-glass-white border border-glass-border text-sm font-medium text-text-secondary backdrop-blur-xl mb-4">
            Why Helium
          </span>
          <h2 className="font-heading font-extrabold text-text-primary mb-4" style={{ fontSize: "clamp(28px, 3.5vw, 44px)", letterSpacing: "-0.02em" }}>
            Built for brands that{" "}
            <span className="gradient-text">refuse to settle</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Premium China fulfilment without the communication chaos
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-50px" }}
        >
          {bentoItems?.map((item) => (
            <motion.div
              key={item?.id}
              variants={cardVariant}
              className={`${item?.size} glass-card p-8 group ${item?.accent ? 'bg-gradient-card' : ''}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-glass-white border border-glass-border flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-smooth">
                  <Icon name={item?.icon} size={24} className="text-accent-primary" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wide text-accent-primary">{item?.label}</span>
              </div>
              <h3 className="text-2xl font-bold text-text-primary mb-3">{item?.title}</h3>
              <p className="text-text-secondary leading-relaxed">{item?.body}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}